import { CSSProperties, useState } from "react";
import styled from "styled-components";
import media from "styled-media-query";
import { User, UserScheme } from "client-v2";

import {
	Modal,
	Input,
	ColorV2,
	Text,
	Button,
	Form,
} from "@adoptaunabuelo/react-components";
import { useSelector } from "react-redux";
import { State } from "../../../redux";
import { ShieldCheck, X } from "lucide-react";
import { useGoogleMaps } from "../../../layout/GoogleMapsContext";

const ModalContainer = styled.div`
	display: flex;
	flex-direction: row;
	${media.lessThan("medium")`
        flex-direction: column;
	`}
`;
const ColLeft = styled.div`
	display: flex;
	flex-direction: column;
	background-color: ${ColorV2.surface.background};
	width: 280px;
	padding: 40px 32px 32px;
	gap: 16px;
	border-radius: 0px 24px 24px 0px;
	${media.lessThan("medium")`
        width: unset;
        border-radius: 0px 0px 24px 24px;
        padding: 40px 24px 32px;
	`}
`;
const ColRight = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
	padding: 40px 32px;
	gap: 16px;
	justify-content: center;
	${media.lessThan("medium")`
        padding: 40px 24px;
	`}
`;
const Icon = styled.img`
	height: 64px;
	width: 64px;
	margin-bottom: 16px;
`;
const Row = styled.div`
	display: flex;
	flex-direction: row;
`;
const DataContainer = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	gap: 12px;
	${media.lessThan("medium")`
        margin-bottom: 48px;
	`}
`;

const Relief = (props: Props) => {
	const { isLoaded } = useGoogleMaps();
	const isMobile: boolean = useSelector(
		(state: State) => state.screen.isMobile
	);

	const [loading, setLoading] = useState(false);
	const [idCard, setIdCard] = useState<string | undefined>(undefined);
	const [error, setError] = useState<string | undefined>(undefined);
	const [location, setLocation] = useState<LocationObjProps | undefined>(
		undefined
	);

	const saveIdCard = () => {
		if (idCard) {
			if (validateCardId(idCard)) {
				setLoading(true);
				User.set(props.user.objectId, {
					idCard: idCard,
					...location,
					shortAddress: location?.shortAddress,
				})
					.then((result) => {
						setLoading(false);
						props.onClose();
					})
					.catch((e: string) => {
						setError(e);
						setLoading(false);
					});
			} else {
				setError("El documento introducido no es válido.");
			}
		} else {
			setError(
				"Debes añadir tu documento de identidad antes de continuar."
			);
		}
	};

	const validateCardId = (id: string) => {
		var DNI_REGEX = /^(\d{8})([A-Z])$/;
		var NIE_REGEX = /^[XYZ]\d{7,8}[A-Z]$/;

		// Ensure upcase and remove whitespace
		const str = id.toUpperCase().replace(/\s/, "");

		//Check the id type
		if (str.match(DNI_REGEX)) {
			var dni_letters = "TRWAGMYFPDXBNJZSQVHLCKE";
			var letter = dni_letters.charAt(parseInt(str, 10) % 23);
			return letter == str.charAt(8);
		} else if (str.match(NIE_REGEX)) {
			// Change the initial letter for the corresponding number and validate as DNI
			var nie_prefix = str.charAt(0);
			switch (nie_prefix) {
				case "X":
					nie_prefix = "0";
					break;
				case "Y":
					nie_prefix = "1";
					break;
				case "Z":
					nie_prefix = "2";
					break;
			}
			const tempStr = nie_prefix + str.substr(1);

			//Validate NIE as DNI
			var dni_letters = "TRWAGMYFPDXBNJZSQVHLCKE";
			var letter = dni_letters.charAt(parseInt(tempStr, 10) % 23);
			return letter == tempStr.charAt(8);
		} else return false;
	};

	const onLocationChange = async (item: {
		data?: LocationObjProps;
		error?: string;
	}) => {
		setError("");
		setLocation(item.data);
	};

	return (
		<Modal
			isVisible={props.isVisible}
			style={{ width: 820 }}
			type={isMobile ? "full-screen" : "default"}
			contentStyle={{ padding: 0 }}
			hideClose={true}
			error={error}
			onClose={props.onClose}
		>
			<ModalContainer>
				<ColLeft>
					<Icon
						src={require("../../../assets/images/icon/coins.webp")}
					/>
					<Text type="h3" weight="semibold">
						¡No olvides añadir tus datos para poder deducirte tu
						donación!
					</Text>
					<Text type="p2">
						Así, cuando hagas la Declaración de la Renta recuperarás
						automáticamente hasta el 80% de tus donaciones.
					</Text>
					{!props.hidePresent && (
						<Text type="p2" weight="semibold">
							¡Y recibirás un regalito de nuestra parte!
						</Text>
					)}
				</ColLeft>
				<ColRight>
					<Button
						style={{
							position: "absolute",
							top: 8,
							right: 8,
							backgroundColor: "white",
							borderRadius: 48,
						}}
						design={"image"}
						icon={<X color={ColorV2.text.neutralHard} />}
						onClick={props.onClose}
					/>
					<DataContainer>
						<Text type="p" weight="medium">
							Documento de identidad
						</Text>
						<Input
							type="text"
							placeholder="DNI o NIE"
							design="secondary"
							onChange={(e) => {
								setError(undefined);
								setIdCard(e.target.value);
							}}
						/>
						{!props.hideAddress && (
							<>
								<Text
									type="p"
									weight="medium"
									style={{ marginTop: 12 }}
								>
									Dirección completa
								</Text>
								<Form
									type="location"
									isLoaded={isLoaded}
									design="secondary"
									onSubmit={onLocationChange}
								/>
							</>
						)}
					</DataContainer>
					<Row style={{ gap: 12 }}>
						<ShieldCheck
							height={20}
							width={20}
							color={ColorV2.text.neutralMedium}
						/>
						<Text
							type="c2"
							style={{
								display: "flex",
								flex: 1,
								color: ColorV2.text.neutralMedium,
							}}
						>
							Tus datos están protegidos y solo se usarán con el
							fin de que puedas deducirte tu donación y recibir tu
							regalo en casa
						</Text>
					</Row>
					<Button
						style={{ position: "sticky", bottom: 24 }}
						loading={loading}
						onClick={saveIdCard}
					>
						Guardar
					</Button>
				</ColRight>
			</ModalContainer>
		</Modal>
	);
};
export default Relief;
export interface Props {
	style?: CSSProperties;
	isVisible: boolean;
	user: UserScheme;
	hideAddress?: boolean;
	hidePresent?: boolean;
	onClose: () => void;
}

interface LocationObjProps {
	address?: string;
	shortAddress?: string;
	route?: string;
	routeNumber?: string;
	routeInfo?: string;
	city?: string;
	province?: string;
	zipCode?: string;
	country?: string;
	location?: google.maps.LatLngLiteral;
	timeZone?: string;
}
